/* Copyright (c) 2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Abstraction interface for web worker services
 * Implementations can use web workers, setTimeout, or other fallback mechanisms
 * This provides access to background processing capabilities like timers, hashing, etc.
 */
export interface WebWorkerService {
    /**
     * Initializes the web worker instance
     * Can be called multiple times if separate worker instances are needed
     */
    initialize(): any;

    /**
     * Handles one-time timer operations
     * @param inputData Timer configuration (start/stop and timeout duration)
     * @param callback Callback function to be invoked when timer fires
     * @returns Observable that emits timer state updates
     */
    intervalHandler(inputData: any, callback: any): Observable<string>;

    /**
     * Handles periodic interval operations
     * @param inputData Interval configuration
     * @param callback Callback function to be invoked on each interval tick
     * @returns Observable that emits interval state updates
     */
    periodicIntervalHandler(inputData: any, callback: any): Observable<string>;

    /**
     * Computes hash of input data
     * @param inputData Data to be hashed
     * @returns Observable that emits the computed hash
     */
    hash(inputData: any): Observable<string>;
}

/**
 * Injection token for WebWorkerService
 * Applications can provide their own implementation of this service
 */
export const WEB_WORKER_ADAPTER = new InjectionToken<WebWorkerService>('WebWorkerAdapter');
